import { friendlyError } from '../../utils/userMessages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment, LinearProgress,
  MenuItem, Paper, Stack, TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import {
  AutoAwesome, Business, Inventory2 as Category, Close, Public as Explore, SettingsApplications as Hub, Insights, LocationOn,
  People as PersonSearch, Public, Refresh, Search, Send, ShoppingBagRounded as ShoppingBag, Stars, Public as TravelExplore, BadgeOutlined as Work,
} from '@mui/icons-material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import toast from 'react-hot-toast';
import Header from '../common/Header/Header';
import LoadingSpinner from '../common/LoadingSpinner';
import { db } from '../../config/firebase';
import { useAuth } from '../auth/AuthContext';
import { resolveCompanyId, getCompanyCoverUrl, getCompanyLogoUrl, getPersonPhotoUrl } from '../../utils/companyIdentity';
import { brandPalette as palette, getContrastText } from '../../utils/brandPalette';
import { asList, asText, calculateLocalBusinessFit, compactList, fullUserName, userBusinessProfile } from '../../utils/businessProfile';
import { buildBusinessIntroductionDraft } from '../../utils/aiFallback';
import { generateIntroduction, regenerateMyMatches } from '../../services/integrations/ai';
import { introductionService } from '../../services/workflow/communication';
import EntityRatingButton from '../common/EntityRatingButton';

const COLORS = ['#2F6FED', '#8B4DE8', '#13A773', '#F39A24', '#E84B7A', '#0F9E9A', '#5B58D6', '#C047A8'];

const MatchRing = ({ score = 0, color }) => (
  <Box sx={{ width: 50, height: 50, borderRadius: '50%', p: '5px', background: `conic-gradient(${color} ${Math.max(0, Math.min(100, score)) * 3.6}deg, ${color}18 0deg)`, boxShadow: `0 8px 20px ${color}18` }}>
    <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.96)', border: `1px solid ${color}18` }}>
      <Box sx={{ textAlign: 'center' }}><Typography sx={{ fontSize: 11.5, lineHeight: 1, fontWeight: 950, color }}>{score}%</Typography><Typography sx={{ mt: .15, fontSize: 7, fontWeight: 850, color: '#8A98A8', textTransform: 'uppercase', letterSpacing: '.06em' }}>Fit</Typography></Box>
    </Box>
  </Box>
);

const DataRow = ({ icon, label, value, color }) => (
  <Stack direction="row" spacing={.6} sx={[{ alignItems: 'flex-start' }, { minWidth: 0 }]}>
    <Box sx={{ width: 21, height: 21, flex: '0 0 auto', borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: `${color}0F`, color, border: `1px solid ${color}18`, '& svg': { fontSize: 11.5 } }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: 7.6, color: '#91A0B0', fontWeight: 850, letterSpacing: '.055em', textTransform: 'uppercase' }}>{label}</Typography><Typography sx={{ mt: .1, fontSize: 9.7, color: '#425168', fontWeight: 720, lineHeight: 1.3 }}>{value || 'Not specified'}</Typography></Box>
  </Stack>
);

const PremiumPersonDirectoryCard = ({ user, companyEntity, color, score, knownMatch, onProfile, onIntro }) => {
  const p = userBusinessProfile(user);
  const name = fullUserName(user) || 'Business Professional';
  const initials = name.split(/\s+/).slice(0, 2).map((part) => part?.[0] || '').join('').toUpperCase() || 'P';
  const hasCompanyEntity = Boolean(companyEntity && Object.keys(companyEntity).length);
  const companyCover = hasCompanyEntity ? getCompanyCoverUrl(companyEntity) : (user.companyCoverImage || user.companyCoverUrl || '');
  const companyLogo = hasCompanyEntity ? getCompanyLogoUrl(companyEntity) : (user.companyLogo || user.companyLogoUrl || '');
  const personPhoto = getPersonPhotoUrl(user);
  const scoreColor = score >= 75 ? '#13A773' : score >= 55 ? '#2F6FED' : score >= 35 ? '#F39A24' : color;
  const isActive = user.isActive !== false && user.isBlocked !== true;

  return (
    <Card
      elevation={0}
      className="tf-color-card tf-company-directory-card tf-premium-company-card tf-company-design-card tf-premium-person-directory-card"
      sx={{
        '--card-color': color,
        minHeight: 270,
        height: '100%',
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(circle at 94% 10%, ${alpha(color,.12)}, transparent 30%), linear-gradient(145deg, rgba(255,255,255,.995), ${alpha(color,.06)}) !important`,
        border: `1px solid ${alpha(color,.13)} !important`,
        boxShadow: `0 12px 30px rgba(25,57,78,.085), 0 3px 9px ${alpha(color,.04)} !important`,
        transition: 'transform .22s cubic-bezier(0.22,1,0.36,1), box-shadow .22s ease, border-color .22s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: `${alpha(color,.24)} !important`,
          boxShadow: `0 20px 42px ${alpha(color,.15)}, 0 6px 14px rgba(25,57,78,.06) !important`,
          '& .person-card-avatar': { transform: 'scale(1.06) rotate(-2deg)' },
        },
      }}
    >
      <Box className="tf-card-dotgrid" sx={{ right: 11, top: 11, opacity: .08 }} />
      <Box sx={{ position: 'absolute', width: 145, height: 145, right: -52, top: -58, borderRadius: '50%', bgcolor: alpha(color,.08), pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', width: 110, height: 110, right: 28, bottom: -72, borderRadius: '36% 64% 62% 38%', bgcolor: alpha(color,.055), transform: 'rotate(26deg)', pointerEvents: 'none' }} />
      <Work sx={{ position: 'absolute', right: 14, bottom: 5, fontSize: 72, color: alpha(color,.055), transform: 'rotate(-8deg)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${color}, ${alpha(color,.45)}, transparent 88%)`, zIndex: 3 }} />

      <Box className="tf-fixed-cover tf-directory-cover" sx={{
        height: 92,
        position: 'relative',
        zIndex: 1,
        background: companyCover
          ? `linear-gradient(180deg, ${alpha(color,.04)}, ${alpha(color,.14)}), url("${companyCover}") center/cover no-repeat`
          : `linear-gradient(118deg, ${alpha(color,.92)}, ${alpha(palette.purple,.76)})`,
        borderBottom: `1px solid ${alpha(color,.12)}`,
        overflow: 'hidden',
      }}>
        {!companyCover && <Business sx={{ position:'absolute', right:10, bottom:-15, fontSize:72, color:'rgba(255,255,255,.13)', transform:'rotate(-10deg)' }} />}
        <Box sx={{ position:'absolute', inset:0, background:'linear-gradient(90deg,rgba(255,255,255,.04),rgba(255,255,255,.22),rgba(255,255,255,.03))', opacity:.55 }} />
      </Box>

      <CardContent sx={{ p: '0 13px 13px !important', position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, mt: -3.2 }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={personPhoto || undefined}
              className="person-card-avatar"
              sx={{
                width: 58, height: 58, borderRadius: '15px', flexShrink: 0,
                color: getContrastText(color), fontSize: '1rem', fontWeight: 850,
                background: `linear-gradient(145deg,${color},${alpha(color,.68)})`,
                border: '3px solid #fff',
                boxShadow: `0 8px 18px ${alpha(color,.24)}`,
                transition: 'transform .3s cubic-bezier(0.34,1.56,0.64,1)',
              }}
            >{initials}</Avatar>
            {companyLogo && <Avatar src={companyLogo} variant="rounded" sx={{ position:'absolute', right:-7, bottom:-5, width:22, height:22, borderRadius:'6px', bgcolor:'#fff', border:'2px solid #fff', boxShadow:'0 4px 9px rgba(25,57,78,.16)', '& img':{ objectFit:'contain', padding:'1px' } }} />}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={.45} sx={{ alignItems:'center', minWidth:0 }}>
              <Typography sx={{ fontSize: '.78rem', fontWeight: 850, color: palette.ink, lineHeight: 1.2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{name}</Typography>
              {user.isVerified && <Stars sx={{ color:'#E9A220', fontSize:14, flexShrink:0 }} />}
            </Stack>
            <Typography sx={{ mt:.25, fontSize:'.52rem', color:palette.inkFaint, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.jobTitle || 'Business Professional'} · {p.companyName || 'Independent'}</Typography>
            <Box sx={{ display:'flex', gap:.45, mt:.55, flexWrap:'wrap' }}>
              <Box sx={{ px:.7, height:19, borderRadius:'999px', display:'inline-flex', alignItems:'center', color:color, bgcolor:alpha(color,.075), border:`1px solid ${alpha(color,.10)}`, fontSize:'.48rem', fontWeight:750 }}>{p.country || 'Global'}</Box>
              <Box sx={{ px:.7, height:19, borderRadius:'999px', display:'inline-flex', alignItems:'center', gap:.35, color:isActive ? '#13A773' : palette.inkFaint, bgcolor:alpha(isActive ? '#13A773' : palette.inkFaint,.065), border:`1px solid ${alpha(isActive ? '#13A773' : palette.inkFaint,.10)}`, fontSize:'.48rem', fontWeight:750 }}><Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:isActive ? '#13A773' : palette.inkFaint }} />{isActive ? 'Active' : 'Inactive'}</Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt:1, p:.8, borderRadius:'10px', bgcolor:alpha(color,.04), border:`1px solid ${alpha(color,.075)}` }}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:.8 }}>
            <Typography sx={{ fontSize:'.5rem', fontWeight:760, color:palette.inkLight, textTransform:'uppercase', letterSpacing:'.055em' }}>AI Match</Typography>
            <Typography sx={{ fontSize:'.7rem', fontWeight:850, color:scoreColor }}>{Math.max(0, Math.min(100, score))}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, score))} sx={{ mt:.45, height:4, borderRadius:99, bgcolor:alpha(scoreColor,.10), '& .MuiLinearProgress-bar':{ borderRadius:99, background:`linear-gradient(90deg,${alpha(scoreColor,.72)},${scoreColor})` } }} />
        </Box>

        <Box sx={{ mt:.8, display:'grid', gridTemplateColumns:'1fr 1fr', gap:.55 }}>
          <Box sx={{ minWidth:0, display:'flex', alignItems:'center', gap:.45, color:palette.inkLight }}><Category sx={{ fontSize:12, color, flexShrink:0 }} /><Typography sx={{ fontSize:'.5rem' }} noWrap>{compactList(p.specialization, 2) || 'Specialization'}</Typography></Box>
          <Box sx={{ minWidth:0, display:'flex', alignItems:'center', gap:.45, color:palette.inkLight }}><Explore sx={{ fontSize:12, color, flexShrink:0 }} /><Typography sx={{ fontSize:'.5rem' }} noWrap>{compactList(p.primaryServices, 2) || 'Services'}</Typography></Box>
          <Box sx={{ minWidth:0, display:'flex', alignItems:'center', gap:.45, color:palette.inkLight }}><TravelExplore sx={{ fontSize:12, color, flexShrink:0 }} /><Typography sx={{ fontSize:'.5rem' }} noWrap>{compactList([...p.primaryRegions, ...p.secondaryRegions], 2) || p.country || 'Global'}</Typography></Box>
          <Box sx={{ minWidth:0, display:'flex', alignItems:'center', gap:.45, color:palette.inkLight }}><ShoppingBag sx={{ fontSize:12, color, flexShrink:0 }} /><Typography sx={{ fontSize:'.5rem' }} noWrap>{compactList([...p.primaryNeeds, ...p.secondaryNeeds], 2) || 'Opportunities'}</Typography></Box>
        </Box>

        {knownMatch?.reasons?.length ? <Typography sx={{ mt:.7, fontSize:'.48rem', color:palette.inkFaint, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{knownMatch.reasons[0]}</Typography> : null}

        <Box sx={{ mt:'auto', pt:.9, display:'flex', alignItems:'center', gap:.55 }}>
          <Button size="small" onClick={onIntro} startIcon={<AutoAwesome sx={{ fontSize:13 }} />} sx={{ minHeight:28, flex:1, color:'#fff !important', background:`linear-gradient(135deg,${color},${palette.purple}) !important`, fontSize:'.55rem !important', boxShadow:`0 7px 16px ${alpha(color,.20)} !important` }}>AI Intro</Button>
          <EntityRatingButton compact targetType="user" targetId={user.id} targetName={name} target={user} color="#E9A220" />
          <Tooltip title="View profile"><IconButton onClick={onProfile} sx={{ width:29, height:29, borderRadius:'9px', color, bgcolor:alpha(color,.08), border:`1px solid ${alpha(color,.12)}`, boxShadow:'0 3px 9px rgba(25,57,78,.04)' }}><Public sx={{ fontSize:14 }} /></IconButton></Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};


const UserDirectory = () => {
  const navigate = useNavigate();
  const { currentUser, userData, companyData } = useAuth();
  const companyId = resolveCompanyId({ currentUser, userData, companyData });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [matchMap, setMatchMap] = useState({});
  const [companyMap, setCompanyMap] = useState({});
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [industry, setIndustry] = useState('');
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState('');
  const [matchAnalysis, setMatchAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let userSnap;
      try { userSnap = await getDocs(query(collection(db, 'users'), where('isActive', '==', true))); }
      catch (_) { userSnap = await getDocs(collection(db, 'users')); }
      const list = userSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => u.id !== currentUser?.uid && u.isBlocked !== true && u.isActive !== false && u.isApproved !== false)
        .sort((a, b) => fullUserName(a).localeCompare(fullUserName(b), undefined, { sensitivity: 'base' }));
      setUsers(list);

      // Resolve associated company media once so every person card can carry the
      // same cover/profile identity that is visible in the Company Directory.
      try {
        const companySnap = await getDocs(collection(db, 'companies'));
        const nextCompanies = {};
        companySnap.docs.forEach((snap) => {
          const row = { id: snap.id, ...snap.data() };
          nextCompanies[snap.id] = row;
          if (row.companyId) nextCompanies[String(row.companyId)] = row;
          const nameKey = asText(row.companyName || row.name).trim().toLowerCase();
          if (nameKey) nextCompanies[`name:${nameKey}`] = row;
        });
        setCompanyMap(nextCompanies);
      } catch (companyError) {
        console.warn('Associated company media unavailable:', companyError);
        setCompanyMap({});
      }

      if (currentUser?.uid) {
        try {
          const matches = await getDocs(query(collection(db, 'business_matches'), where('ownerUserId', '==', currentUser.uid)));
          const next = {};
          matches.docs.forEach((snap) => {
            const row = snap.data() || {};
            if (row.matchedUserId) next[row.matchedUserId] = { id: snap.id, ...row };
          });
          setMatchMap(next);
        } catch (error) {
          console.warn('Business match scores unavailable:', error);
          setMatchMap({});
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(friendlyError(error, 'Unable to load user directory.'));
    } finally { setLoading(false); }
  }, [currentUser?.uid]);

  useEffect(() => { load(); }, [load]);

  const refreshNetwork = async () => {
    if (!companyId) return load();
    setLoading(true);
    try {
      await regenerateMyMatches(companyId);
      await load();
      toast.success('AI business matches refreshed from services, needs, specialization and regions.');
    } catch (error) {
      console.error(error);
      toast.error(friendlyError(error, 'Unable to refresh AI matches.'));
      await load();
    }
  };

  const countries = useMemo(() => [...new Set(users.map((u) => asText(u.country || u.countryName)).filter(Boolean))].sort(), [users]);
  const industries = useMemo(() => [...new Set(users.flatMap((u) => asList(u.specialization || u.businessAreas || u.industries || u.industry)).map(asText).filter(Boolean))].sort(), [users]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((u) => {
      const p = userBusinessProfile(u);
      const haystack = [fullUserName(u), p.jobTitle, p.companyName, p.country, ...p.specialization, ...p.primaryServices, ...p.secondaryServices, ...p.primaryNeeds, ...p.secondaryNeeds, ...p.primaryRegions, ...p.secondaryRegions].join(' ').toLowerCase();
      const countryOk = !country || p.country === country;
      const industryOk = !industry || p.specialization.some((v) => asText(v) === industry);
      return (!needle || haystack.includes(needle)) && countryOk && industryOk;
    });
  }, [users, search, country, industry]);

  const openIntro = async (user) => {
    if (!companyId) return toast.error('Your company profile is still loading.');
    setSelected(user); setDraft(''); setMatchAnalysis(null); setAiLoading(true);
    try {
      const result = await generateIntroduction({
        companyId,
        targetUserId: user.id,
        targetCompanyId: user.companyId,
        context: { directory: 'user', recipientName: fullUserName(user), source: 'people_directory' },
      });
      const resolvedMatch = result?.matchAnalysis || effectiveMatchFor(user) || null;
      setMatchAnalysis(resolvedMatch);
      setDraft(result?.text || buildBusinessIntroductionDraft({ senderUser: userData || {}, targetUser: user, senderCompany: companyData || {}, match: resolvedMatch }));
    } catch (error) {
      console.error(error);
      const localMatch = effectiveMatchFor(user) || null;
      setMatchAnalysis(localMatch);
      setDraft(buildBusinessIntroductionDraft({ senderUser: userData || {}, targetUser: user, senderCompany: companyData || {}, match: localMatch }));
      toast.error('ChatGPT is temporarily unavailable. A business-fit draft was generated locally so you can still review and send it.', { duration: 6000 });
    } finally { setAiLoading(false); }
  };

  const sendIntro = async () => {
    if (!selected || !draft.trim()) return;
    setSending(true);
    try {
      await introductionService.send(companyId, {
        targetUserId: selected.id,
        targetCompanyId: selected.companyId || '',
        message: draft.trim(),
        context: { source: 'people_directory', matchScore: matchAnalysis?.score || matchMap[selected.id]?.score || 0 },
      });
      toast.success(`Introduction sent to ${fullUserName(selected)}. They control whether chat or calendar access is enabled.`);
      setSelected(null); setDraft(''); setMatchAnalysis(null);
    } catch (error) { toast.error(friendlyError(error, 'Unable to send introduction.')); }
    finally { setSending(false); }
  };

  const effectiveMatchFor = useCallback((targetUser) => {
    const stored = matchMap[targetUser?.id];
    if (stored && Number.isFinite(Number(stored.score))) return stored;
    return calculateLocalBusinessFit(userData || {}, targetUser || {}, companyData || {}, {});
  }, [matchMap, userData, companyData]);

  const totalHighFit = useMemo(() => users.filter((u) => Number(effectiveMatchFor(u)?.score || 0) >= 55).length, [users, effectiveMatchFor]);

  return <Box className="erp-page-shell erp-internal-page tf-people-page" sx={{ minHeight: '100vh', bgcolor: palette.canvas }}>
    <Header />
    <Container maxWidth={false} sx={{ maxWidth: 1660, py: { xs: 2, md: 2.5 }, px: { xs: 1.4, sm: 2.2, md: 3 } }}>
      <Box className="tf-command-hero tf-people-command-hero" sx={{ mb: 1.7 }}>
        <Box className="tf-command-hero-orb tf-command-hero-orb-a" /><Box className="tf-command-hero-orb tf-command-hero-orb-b" />
        <Stack direction={{ xs: 'column', lg: 'row' }} sx={[{ alignItems: { xs: 'flex-start', lg: 'center' }, justifyContent: 'space-between', gap: 2 }, { position: 'relative', zIndex: 2 }]}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Box className="tf-command-icon" sx={{ '--command-color': '#2F6FED' }}><PersonSearch /></Box>
            <Box><Typography className="tf-command-eyebrow">PEOPLE · BUSINESS NETWORK</Typography><Typography className="tf-command-title">Discover the right people to do business with</Typography><Typography className="tf-command-subtitle">Profiles are ranked by business relevance. Open a profile to review specialization, services, regions and what they are looking for before sending an AI introduction.</Typography></Box>
          </Stack>
          <Stack direction="row" spacing={.8} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Chip icon={<Hub />} label={`${users.length} professionals`} className="tf-command-chip" />
            <Chip icon={<Insights />} label={`${totalHighFit} strong matches`} className="tf-command-chip" />
            <Button startIcon={<Refresh />} onClick={refreshNetwork} className="tf-command-ghost-btn">Refresh AI Matches</Button>
          </Stack>
        </Stack>
      </Box>

      <Box className="tf-section-surface tf-directory-filter" sx={{ p: 1.25, mb: 1.55 }}>
        <Grid container spacing={1} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, specialization, service or region…" slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth select size="small" label="Country" value={country} onChange={(e) => setCountry(e.target.value)}><MenuItem value="">All countries</MenuItem>{countries.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}</TextField></Grid>
          <Grid size={{ xs: 6, md: 3 }}><TextField fullWidth select size="small" label="Specialization" value={industry} onChange={(e) => setIndustry(e.target.value)}><MenuItem value="">All specializations</MenuItem>{industries.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}</TextField></Grid>
        </Grid>
      </Box>

      {loading ? <LoadingSpinner message="Building your people network…" /> : filtered.length === 0 ? <Box className="tf-section-surface" sx={{ p: 5, textAlign: 'center' }}><PersonSearch sx={{ fontSize: 42, color: '#A4B2C1' }} /><Typography sx={{ mt: 1, fontWeight: 900, color: '#26354A' }}>No matching professionals</Typography><Typography sx={{ mt: .28, fontSize: 12, color: '#7B899A' }}>Try another service, region, specialization or company name.</Typography></Box> : <Grid container spacing={1.05}>
        {filtered.map((user, index) => {
          const color = COLORS[index % COLORS.length];
          const p = userBusinessProfile(user);
          const knownMatch = effectiveMatchFor(user) || {};
          const score = Math.round(Number(knownMatch.score || 0));
          const companyEntity = user.companyId
            ? (companyMap[user.companyId] || {})
            : (companyMap[`name:${asText(p.companyName).trim().toLowerCase()}`] || {});
          const hasCompanyEntity = Boolean(companyEntity && Object.keys(companyEntity).length);
          const companyCover = hasCompanyEntity ? getCompanyCoverUrl(companyEntity) : (user.companyCoverImage || user.companyCoverUrl || '');
          const companyLogo = hasCompanyEntity ? getCompanyLogoUrl(companyEntity) : (user.companyLogo || user.companyLogoUrl || '');
          return <Grid key={user.id} size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
            <PremiumPersonDirectoryCard
              user={user}
              companyEntity={companyEntity}
              color={color}
              score={score}
              knownMatch={knownMatch}
              onProfile={(event) => { event?.stopPropagation?.(); navigate(`/dashboard/users/${user.id}`); }}
              onIntro={(event) => { event?.stopPropagation?.(); openIntro(user); }}
            />
          </Grid>;
        })}
      </Grid>}
    </Container>

    <Dialog open={Boolean(selected)} onClose={() => !sending && setSelected(null)} fullWidth maxWidth="md" slotProps={{ paper: { className: 'tf-premium-dialog' } }}>
      <DialogTitle sx={{ p: 2, pb: 1.2 }}><Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}><Box><Typography sx={{ fontSize: 17, fontWeight: 950, color: '#1B293C' }}>AI Business Introduction</Typography><Typography sx={{ fontSize: 10.8, color: '#718197' }}>ChatGPT uses both profiles and company needs to create a relevant first-contact message.</Typography></Box><IconButton onClick={() => setSelected(null)}><Close /></IconButton></Stack></DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {selected && <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} className="tf-ai-match-panel">
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}><Avatar src={selected.photoURL || selected.profileImage}>{fullUserName(selected)[0]}</Avatar><Box sx={{ minWidth: 0 }}><Typography noWrap sx={{ fontWeight: 900 }}>{fullUserName(selected)}</Typography><Typography noWrap sx={{ fontSize: 9.8, color: '#77869A' }}>{userBusinessProfile(selected).companyName}</Typography></Box></Stack>
              <Divider sx={{ my: 1.2 }} />
              <Typography className="tf-command-eyebrow">AI BUSINESS FIT</Typography>
              <Stack direction="row" spacing={1} sx={[{ alignItems: 'center' }, { mt: .6 }]}><Typography sx={{ fontSize: 31, fontWeight: 950, color: '#2F6FED' }}>{Math.round(Number(matchAnalysis?.score ?? matchMap[selected.id]?.score ?? 0))}%</Typography><Box><Typography sx={{ fontSize: 11, fontWeight: 900, color: '#334258' }}>{matchAnalysis?.level || (Number(matchMap[selected.id]?.score || 0) >= 55 ? 'Strong fit' : 'Potential fit')}</Typography><Typography sx={{ fontSize: 9.5, color: '#8997A7' }}>services · needs · regions · specialization</Typography></Box></Stack>
              <LinearProgress variant="determinate" value={Math.round(Number(matchAnalysis?.score ?? matchMap[selected.id]?.score ?? 0))} sx={{ mt: 1, height: 6, borderRadius: 99, bgcolor: 'rgba(47,111,237,.09)', '& .MuiLinearProgress-bar': { borderRadius: 99, background: 'linear-gradient(90deg,#2F6FED,#8B4DE8)' } }} />
              <Stack spacing={.65} sx={{ mt: 1.2 }}>{(matchAnalysis?.reasons || matchMap[selected.id]?.reasons || []).slice(0, 4).map((reason, i) => <Stack key={i} direction="row" spacing={.65} sx={{ alignItems: 'flex-start' }}><AutoAwesome sx={{ mt: .15, fontSize: 11.5, color: COLORS[i % COLORS.length] }} /><Typography sx={{ fontSize: 9.2, lineHeight: 1.3, color: '#5F6F83', fontWeight: 700 }}>{reason}</Typography></Stack>)}</Stack>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            {aiLoading ? <Box className="tf-ai-writing-state"><CircularProgress size={25} /><Typography sx={{ fontSize: 12, fontWeight: 750 }}>ChatGPT is comparing specialization, primary/secondary services, regions and buying needs…</Typography></Box> : <TextField fullWidth multiline minRows={12} value={draft} onChange={(e) => setDraft(e.target.value)} label="Editable AI introduction" helperText="Review before sending. The recipient decides whether to start chat, share calendar or ignore." />}
          </Grid>
        </Grid>}
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}><Button onClick={() => setSelected(null)}>Cancel</Button><Button variant="contained" startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send />} disabled={sending || aiLoading || !draft.trim()} onClick={sendIntro} sx={{ fontWeight: 900 }}>{sending ? 'Sending…' : 'Send Introduction'}</Button></DialogActions>
    </Dialog>
  </Box>;
};

export default UserDirectory;
